// Rename Tag Dialog Component
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Tag as TagIcon } from 'lucide-react';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import Button from '@/components/Button';
import Input from '@/components/Input';

interface RenameTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tag: { id: number; name: string; color?: string } | null;
}

export default function RenameTagDialog({ open, onOpenChange, tag }: RenameTagDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const queryClient = useQueryClient();

  // Update name when tag changes
  useEffect(() => {
    if (tag) {
      setName(tag.name);
      setError('');
    }
  }, [tag]);

  // Update tag mutation
  const updateTagMutation = useMutation({
    mutationFn: (data: { name: string }) => api.updateTag(tag!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setError('');
      onOpenChange(false);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to rename tag');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter a tag name');
      return;
    }

    if (name.length > 50) {
      setError('Tag name must be 50 characters or less');
      return;
    }

    if (name.trim() === tag?.name) {
      onOpenChange(false);
      return;
    }

    updateTagMutation.mutate({ name: name.trim() });
  };

  if (!tag) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <TagIcon className="h-5 w-5 text-primary" />
            <DialogTitle>Rename Tag</DialogTitle>
          </div>
          <DialogDescription>
            Change the name of this tag
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="tag-name" className="text-sm font-medium">
              Tag Name
            </label>
            <Input
              id="tag-name"
              type="text"
              placeholder="Tag name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={updateTagMutation.isPending}
              maxLength={50}
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateTagMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateTagMutation.isPending}>
              {updateTagMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Renaming...
                </>
              ) : (
                'Rename'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}




